import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { clientsApi, integrationsApi } from '../../services/api';

const PRIORITY_LEVELS = [
  { value: 1, label: '1 – Large continuous support' },
  { value: 2, label: '2 – Large one-off solution or upsell opp' },
  { value: 3, label: '3 – Medium continuous support' },
  { value: 4, label: '4 – Medium solution or upsell opp' },
  { value: 5, label: '5 – Small continuous support' },
  { value: 6, label: '6 – Other' },
];

const EMPTY_FORM = {
  name: '',
  acronym: '',
  priority_level: 6,
  dashboard_path: '',
  logo: null,
  logo_content_type: '',
  logoPreview: null,
  contacts: [],
};

function ClientForm({ client, onSave, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [newContact, setNewContact] = useState({ name: '', email: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || '',
        acronym: client.acronym || '',
        priority_level: client.priority_level || 6,
        dashboard_path: client.dashboard_path || '',
        logo: null,
        logo_content_type: '',
        logoPreview: client.has_logo ? clientsApi.getLogoUrl(client.id) : null,
        contacts: (client.contacts || []).map((c) => ({ ...c })),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [client]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const b64 = dataUrl.split(',')[1];
      setForm((prev) => ({
        ...prev,
        logo: b64,
        logo_content_type: file.type || 'image/png',
        logoPreview: dataUrl,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setForm((prev) => ({ ...prev, logo: '', logo_content_type: '', logoPreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddContact = () => {
    const name = newContact.name.trim();
    const email = newContact.email.trim();
    if (!name || !email) return;
    setForm((prev) => ({ ...prev, contacts: [...prev.contacts, { name, email }] }));
    setNewContact({ name: '', email: '' });
  };

  const handleRemoveContact = (idx) => {
    setForm((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    setError('');
    const acronym = form.acronym.trim().toUpperCase();
    if (!form.name.trim()) { setError('Client name is required'); return; }
    if (!acronym || acronym.length > 4) { setError('Acronym is required and max 4 characters'); return; }

    const payload = {
      name: form.name.trim(),
      acronym,
      priority_level: form.priority_level,
      dashboard_path: form.dashboard_path.trim().toLowerCase() || null,
      contacts: form.contacts,
    };

    // Only include logo if changed
    if (form.logo !== null) {
      payload.logo = form.logo;
      payload.logo_content_type = form.logo_content_type;
    }

    try {
      if (client) {
        await clientsApi.update(client.id, payload);
      } else {
        await clientsApi.create(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Client Name"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          fullWidth
          required
        />

        <Box display="flex" gap={2}>
          <TextField
            label="Acronym"
            value={form.acronym}
            onChange={(e) => handleChange('acronym', e.target.value.toUpperCase().slice(0, 4))}
            inputProps={{ maxLength: 4 }}
            helperText="Up to 4 uppercase letters (e.g. CLR)"
            required
            sx={{ width: 160 }}
          />
          <FormControl fullWidth>
            <InputLabel>Priority Level</InputLabel>
            <Select
              value={form.priority_level}
              onChange={(e) => handleChange('priority_level', e.target.value)}
              label="Priority Level"
            >
              {PRIORITY_LEVELS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="Dashboard Path"
          value={form.dashboard_path}
          onChange={(e) => handleChange('dashboard_path', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 10))}
          inputProps={{ maxLength: 10 }}
          helperText="Up to 10 lowercase URL-safe characters — will be the client portal path"
          fullWidth
        />

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">Logo</Typography>
        </Divider>

        <Box display="flex" alignItems="center" gap={2}>
          {form.logoPreview ? (
            <Avatar
              src={form.logoPreview}
              variant="rounded"
              sx={{ width: 80, height: 80, objectFit: 'contain', bgcolor: 'grey.100' }}
              imgProps={{ style: { objectFit: 'contain' } }}
            />
          ) : (
            <Avatar variant="rounded" sx={{ width: 80, height: 80, bgcolor: 'grey.200' }}>
              <Typography variant="caption" color="text.secondary">No logo</Typography>
            </Avatar>
          )}
          <Box>
            <Button variant="outlined" size="small" component="label">
              {form.logoPreview ? 'Replace Logo' : 'Upload Logo'}
              <input
                type="file"
                hidden
                accept="image/*"
                ref={fileInputRef}
                onChange={handleLogoChange}
              />
            </Button>
            {form.logoPreview && (
              <Button size="small" color="error" onClick={handleRemoveLogo} sx={{ ml: 1 }}>
                Remove
              </Button>
            )}
            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
              Square or rectangular — resized to max 400×400 px
            </Typography>
          </Box>
        </Box>

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">Contacts</Typography>
        </Divider>

        {form.contacts.map((c, idx) => (
          <Box key={idx} display="flex" alignItems="center" gap={1}>
            <Typography sx={{ flexGrow: 1 }}>
              {c.name} &lt;{c.email}&gt;
            </Typography>
            <IconButton size="small" onClick={() => handleRemoveContact(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Box display="flex" gap={1} alignItems="flex-start">
          <TextField
            label="Contact Name"
            value={newContact.name}
            onChange={(e) => setNewContact((p) => ({ ...p, name: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Contact Email"
            value={newContact.email}
            onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleAddContact()}
          />
          <Button variant="outlined" size="small" onClick={handleAddContact} sx={{ mt: 0.5 }}>
            Add
          </Button>
        </Box>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {client ? 'Save Changes' : 'Create Client'}
        </Button>
      </Box>
    </Box>
  );
}

function GitLabImportDialog({ open, onClose, onImported }) {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setResult(null);
    setRows([]);
    setLoading(true);
    integrationsApi.getGitLabProjects()
      .then((res) => {
        setRows(
          res.data.map((p) => ({
            ...p,
            name: p.suggested_name,
            acronym: p.suggested_acronym,
            selected: !p.client_exists,
          }))
        );
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to fetch GitLab projects');
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleRowChange = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const selectedRows = rows.filter((r) => r.selected && !r.client_exists);

  const handleImport = async () => {
    setImporting(true);
    setError('');
    setResult(null);
    try {
      const clients = selectedRows.map((r) => ({
        name: r.name,
        acronym: r.acronym.toUpperCase().slice(0, 4),
        logo_url: r.logo_url,
        priority_level: 6,
      }));
      const res = await integrationsApi.importGitLabClients(clients);
      setResult(res.data);
      if (res.data.created > 0) onImported();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Clients from GitLab</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" alignItems="center" gap={2} py={2}>
            <CircularProgress size={20} />
            <Typography>Fetching GitLab projects…</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <Alert severity={result.created > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
            {result.created} client{result.created !== 1 ? 's' : ''} created
            {result.skipped > 0 ? `, ${result.skipped} skipped` : ''}.
            {result.errors?.length > 0 && ` Errors: ${result.errors.join('; ')}`}
          </Alert>
        )}

        {!loading && rows.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Edit names and acronyms before importing. Projects with an existing matching client cannot be re-imported.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Logo</TableCell>
                  <TableCell>GitLab Project</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell sx={{ width: 110 }}>Acronym</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={idx} sx={{ opacity: row.client_exists ? 0.5 : 1 }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={row.selected}
                        disabled={row.client_exists}
                        onChange={(e) => handleRowChange(idx, 'selected', e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.logo_url ? 'Logo from GitLab' : 'No avatar set'}>
                        <Avatar
                          src={row.logo_url || undefined}
                          variant="rounded"
                          sx={{ width: 32, height: 32, bgcolor: 'grey.200', fontSize: 11 }}
                          imgProps={{ style: { objectFit: 'contain' } }}
                        >
                          {row.acronym?.slice(0, 2)}
                        </Avatar>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {row.gitlab_project_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.name}
                        onChange={(e) => handleRowChange(idx, 'name', e.target.value)}
                        size="small"
                        disabled={row.client_exists}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.acronym}
                        onChange={(e) => handleRowChange(idx, 'acronym', e.target.value.toUpperCase().slice(0, 4))}
                        size="small"
                        disabled={row.client_exists}
                        variant="standard"
                        inputProps={{ maxLength: 4, style: { fontFamily: 'monospace' } }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.client_exists ? (
                        <Chip label="Already exists" size="small" color="default" />
                      ) : (
                        <Chip label="New" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {!loading && rows.length === 0 && !error && (
          <Typography color="text.secondary">No GitLab projects found.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || loading || selectedRows.length === 0}
            startIcon={importing ? <CircularProgress size={16} /> : undefined}
          >
            Import {selectedRows.length > 0 ? `${selectedRows.length} ` : ''}Client{selectedRows.length !== 1 ? 's' : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const loadClients = async () => {
    try {
      const res = await clientsApi.getAll();
      setClients(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClients(); }, []);

  const handleEdit = (client) => {
    setEditingClient(client);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingClient(null);
    setDialogOpen(true);
  };

  const handleSave = () => {
    setDialogOpen(false);
    setEditingClient(null);
    loadClients();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await clientsApi.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      loadClients();
    } catch {
      // ignore
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Clients</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import from GitLab
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
            Add Client
          </Button>
        </Box>
      </Box>

      {clients.length === 0 ? (
        <Typography color="text.secondary">
          No clients yet. Add a client to associate projects to a structured account.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>Acronym</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Dashboard Path</TableCell>
              <TableCell>Contacts</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id} hover>
                <TableCell>
                  {client.has_logo ? (
                    <Avatar
                      src={clientsApi.getLogoUrl(client.id)}
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: 'grey.100' }}
                      imgProps={{ style: { objectFit: 'contain' } }}
                    />
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: 'grey.200', fontSize: 10 }}>
                      {client.acronym}
                    </Avatar>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={client.acronym} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{client.name}</TableCell>
                <TableCell>
                  <Typography variant="caption">{client.priority_level_label}</Typography>
                </TableCell>
                <TableCell>
                  {client.dashboard_path ? (
                    <Typography variant="body2" fontFamily="monospace">
                      /{client.dashboard_path}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {client.contacts?.length || 0} contact{client.contacts?.length !== 1 ? 's' : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(client)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirm(client)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingClient ? 'Edit Client' : 'Add Client'}</DialogTitle>
        <DialogContent dividers>
          <ClientForm
            client={editingClient}
            onSave={handleSave}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.name}</strong>? This will remove the client record.
            Existing projects linked to this client will have their client association cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* GitLab import dialog */}
      <GitLabImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={loadClients}
      />
    </Box>
  );
}

export default ClientsTab;
